import { dbg } from '../debugOverlay.js';

/**
 * Server-brokered Google sign-in for the Android shell. The app can't complete a WebView OAuth
 * round-trip (tauri-plugin-deep-link doesn't surface custom-scheme redirects), so the server runs the
 * OAuth: we start a session, open the consent URL in the system browser, then POLL the server until
 * it has minted a Firebase custom token. No deep link — the return trip is a poll. The caller signs
 * in with the returned custom token.
 */
export interface MobileSignInDeps {
  fetchImpl: typeof fetch;
  openUrl: (url: string) => Promise<void>;
  /** Base URL of the server-api. */
  apiUrl: string;
  pollIntervalMs?: number;
  timeoutMs?: number;
  now?: () => number;
  sleep?: (ms: number) => Promise<void>;
}

export async function brokeredGoogleSignIn(deps: MobileSignInDeps): Promise<string> {
  const interval = deps.pollIntervalMs ?? 2000;
  const timeout = deps.timeoutMs ?? 180_000;
  const now = deps.now ?? (() => Date.now());
  const sleep = deps.sleep ?? ((ms: number) => new Promise((r) => setTimeout(r, ms)));
  const base = deps.apiUrl.replace(/\/$/, '');

  dbg('mobile sign-in: starting session');
  const startRes = await deps.fetchImpl(`${base}/mobile-auth/start`, { method: 'POST' });
  if (!startRes.ok) throw new Error(`Could not start sign-in (${startRes.status})`);
  const { authUrl, sessionId } = (await startRes.json()) as { authUrl: string; sessionId: string };

  dbg('opening browser for consent');
  await deps.openUrl(authUrl);

  const deadline = now() + timeout;
  let polls = 0;
  while (now() < deadline) {
    await sleep(interval);
    polls += 1;
    try {
      const res = await deps.fetchImpl(`${base}/mobile-auth/poll?session=${encodeURIComponent(sessionId)}`);
      if (res.ok) {
        const { token } = (await res.json()) as { token: string | null };
        if (token) {
          dbg(`got custom token after ${polls} polls`);
          return token;
        }
      }
    } catch (e) {
      dbg(`poll ${polls} error: ${String(e)}`);
    }
  }
  throw new Error('Sign-in timed out — please try again.');
}
